import React, { useLayoutEffect, useRef } from "react";

import { Card, DatePicker, Grid } from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";

import { useFiltersContext } from "@/hooks/useFiltersContext";

import { ClientSelect, ProjectSelect } from "@/components/molecules";

import { TCompany, TProject } from "@/types";

import { cn, parseDay } from "@/utils";

import { ReportSummaryBar } from "./ReportSummaryBar";

interface IReportFiltersCardProps {
	className?: string;
}

export const ReportFiltersCard: React.FC<IReportFiltersCardProps> = ({
	className,
}) => {
	const { filters, setFilter } = useFiltersContext("reports");
	const cardRef = useRef<HTMLDivElement>(null);
	const summaryRef = useRef<HTMLDivElement>(null);

	// Stuck, only the summary bar stays in view: the card sticks with a
	// negative top that pushes everything above the bar's content (filters,
	// the bar's own divider) past the top edge, keeping the card's padding
	// above it. Measured, not a fixed offset: the filter grid stacks into a
	// single column on narrow screens, which made a desktop-tuned offset
	// leave the date pickers in view on mobile. Re-measured whenever the
	// card resizes (grid reflow, viewport).
	useLayoutEffect(() => {
		const card = cardRef.current;
		const summary = summaryRef.current;
		if (!card || !summary) return;

		const place = () => {
			const bar = (summary.firstElementChild as HTMLElement) ?? summary;
			const cardStyle = getComputedStyle(card);
			const barStyle = getComputedStyle(bar);
			const hidden =
				(parseFloat(cardStyle.borderTopWidth) || 0) +
				summary.offsetTop +
				(parseFloat(barStyle.borderTopWidth) || 0) +
				(parseFloat(barStyle.paddingTop) || 0);
			const padding = parseFloat(cardStyle.paddingTop) || 0;
			card.style.top = `${padding - hidden}px`;
		};

		place();
		const observer = new ResizeObserver(place);
		observer.observe(card);
		return () => observer.disconnect();
	}, []);

	// A report covers a client (every project of theirs) or one project
	// (docs/modele.md, phase 6). Picking a client clears the project,
	// which belonged to the previous one.
	const scopeByClient = (client: TCompany | null) => {
		setFilter("client", client);
		setFilter("project", undefined);
	};

	return (
		// Sticky on the whole card: a sticky element only sticks within its
		// parent, so the summary bar alone could not outlive the card. z-10
		// keeps the card (and the selects' inline listboxes) above the
		// project cards it scrolls over.
		<Card
			ref={cardRef}
			bordered
			elevation={1}
			className={cn(["sticky z-10", className])}
		>
			<Grid columns={2} minItemWidth="220px" gap="0.75rem">
				<ClientSelect
					autoSelectSingle={false}
					label={t("reports.byClient")}
					placeholder={t("reports.byClientPlaceholder")}
					value={filters.client ?? null}
					onChange={(value) =>
						scopeByClient((value as TCompany | null) ?? null)
					}
				/>
				<ProjectSelect
					value={filters.project}
					key={filters.client?.id ?? ""}
					clientId={filters.client?.id}
					reportable
					onChange={(value) =>
						setFilter("project", value as TProject | null)
					}
				/>
				<DatePicker
					onChange={(value) =>
						setFilter(
							"dateFrom",
							value ? format(value, "yyyy-MM-dd") : undefined
						)
					}
					label={t("reports.since")}
					value={filters.dateFrom ? parseDay(filters.dateFrom) : null}
					placeholder={t("reports.since")}
				/>
				<DatePicker
					label={t("reports.until")}
					onChange={(value) =>
						setFilter(
							"dateTo",
							value ? format(value, "yyyy-MM-dd") : undefined
						)
					}
					value={filters.dateTo ? parseDay(filters.dateTo) : null}
					placeholder={t("reports.until")}
				/>
			</Grid>
			<div ref={summaryRef}>
				<ReportSummaryBar />
			</div>
		</Card>
	);
};
